import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { CheckCircle, Calendar, User, FileText, ArrowLeft, BarChart3, Edit, Download, DollarSign, Archive } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { ProfileType } from '../types';
import { ProjectHealthScore } from '../components/Projects/ProjectHealthScore';

export const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentProject, fetchProject, isLoading, setLastViewedProject, archiveProject } = useProjectStore();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProject(id);
    }
  }, [id, fetchProject]);

  // Set as last viewed when project is loaded
  useEffect(() => {
    if (currentProject) {
      setLastViewedProject(currentProject);
    }
  }, [currentProject, setLastViewedProject]);

  // Show success message if coming from wizard submission
  useEffect(() => {
    if (location.state?.message) {
      setShowSuccessMessage(true);
      // Clear the message after 5 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!currentProject) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Projet non trouvé</h2>
            <p className="text-gray-600 mb-6">Le projet demandé n'existe pas ou vous n'avez pas les droits pour le consulter.</p>
            <button
              onClick={() => navigate('/projects')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour aux projets
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const getStatusColor = (status: string) => {
    const colors = {
      A_PREPARER: 'bg-gray-100 text-gray-800',
      SOUMIS: 'bg-blue-100 text-blue-800',
      ACCORD_PROVISOIRE: 'bg-yellow-100 text-yellow-800',
      VALIDE: 'bg-green-100 text-green-800',
      DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800',
      EN_COURS: 'bg-indigo-100 text-indigo-800',
      VERIFICATION: 'bg-orange-100 text-orange-800',
      TERMINE: 'bg-emerald-100 text-emerald-800',
      REJETE: 'bg-red-100 text-red-800',
      SUSPENDU: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  // Parse wizard data from description if available
  let wizardData = null;
  try {
    if (currentProject.description &&
        currentProject.description.trim().startsWith('{') &&
        currentProject.description.trim().endsWith('}')) {
      wizardData = JSON.parse(currentProject.description);
    }
  } catch (error) {
    // Silently ignore parsing errors for non-JSON descriptions
  }

  const handleArchive = async () => {
    if (!id) return;

    if (!confirm('Voulez-vous vraiment archiver ce projet ? Il ne sera plus visible dans la liste principale des projets.')) {
      return;
    }

    setIsArchiving(true);
    try {
      await archiveProject(id);
      navigate('/projects', {
        state: { message: 'Projet archivé avec succès' }
      });
    } catch (error) {
      console.error('Error archiving project:', error);
      alert('Erreur lors de l\'archivage du projet');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleExportPDF = () => {
    // Create HTML content for PDF
    const projectDate = new Date(currentProject.created_at).toLocaleDateString('fr-FR');
    const lastUpdate = new Date(currentProject.updated_at).toLocaleDateString('fr-FR');

    let descriptionHTML = '';
    if (wizardData?.answers) {
      descriptionHTML = Object.entries(wizardData.answers)
        .map(([key, value]) => `
          <div style="margin-bottom: 12px; padding: 8px; background: #f9fafb; border-radius: 4px;">
            <strong style="color: #374151; text-transform: capitalize;">${key.replace(/_/g, ' ')}:</strong>
            <p style="color: #1f2937; margin-top: 4px;">${value}</p>
          </div>
        `).join('');
    } else if (currentProject.description) {
      descriptionHTML = `<p style="color: #1f2937; line-height: 1.6;">${currentProject.description}</p>`;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Projet ${currentProject.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; }
          .header h1 { color: #1f2937; margin-bottom: 10px; }
          .status-badge { display: inline-block; padding: 4px 12px; border-radius: 16px; font-size: 14px; font-weight: 500; }
          .section { margin-bottom: 30px; }
          .section-title { color: #1f2937; font-size: 18px; font-weight: bold; margin-bottom: 12px; border-left: 4px solid #3b82f6; padding-left: 12px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
          .info-item { padding: 12px; background: #f9fafb; border-radius: 8px; }
          .info-label { font-size: 14px; color: #6b7280; margin-bottom: 4px; }
          .info-value { font-size: 16px; color: #1f2937; font-weight: 500; }
          .documents-list { list-style: none; padding: 0; }
          .documents-list li { padding: 8px; background: #f9fafb; margin-bottom: 8px; border-radius: 4px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${currentProject.name}</h1>
          <span class="status-badge" style="background: ${getStatusColor(currentProject.status).replace('text-', 'color:').replace('bg-', 'background:')}">${getStatusLabel(currentProject.status)}</span>
          <p style="color: #6b7280; margin-top: 12px;">Créé le ${projectDate} • Dernière mise à jour le ${lastUpdate}</p>
        </div>

        <div class="section">
          <div class="section-title">Informations générales</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Porteur de projet</div>
              <div class="info-value">${currentProject.owner?.full_name || currentProject.owner?.email || 'Non renseigné'}</div>
            </div>
            ${currentProject.owner?.organization ? `
            <div class="info-item">
              <div class="info-label">Organisation</div>
              <div class="info-value">${currentProject.owner.organization}</div>
            </div>` : ''}
            ${currentProject.assigned_advisor ? `
            <div class="info-item">
              <div class="info-label">Accompagnateur</div>
              <div class="info-value">${currentProject.assigned_advisor.full_name || currentProject.assigned_advisor.email}</div>
            </div>` : ''}
            ${currentProject.domain ? `
            <div class="info-item">
              <div class="info-label">Domaine</div>
              <div class="info-value">${currentProject.domain}</div>
            </div>` : ''}
          </div>
        </div>

        ${(currentProject.funding_requested || currentProject.funding_amount) ? `
        <div class="section">
          <div class="section-title">Financement</div>
          <div class="info-grid">
            ${currentProject.funding_requested ? `
            <div class="info-item">
              <div class="info-label">Montant demandé</div>
              <div class="info-value">${currentProject.funding_requested.toLocaleString('fr-FR')} €</div>
            </div>` : ''}
            ${currentProject.funding_amount ? `
            <div class="info-item">
              <div class="info-label">Montant accordé</div>
              <div class="info-value" style="color: #10b981;">${currentProject.funding_amount.toLocaleString('fr-FR')} €</div>
            </div>` : ''}
          </div>
        </div>` : ''}

        <div class="section">
          <div class="section-title">Description du projet</div>
          ${descriptionHTML || '<p style="color: #6b7280; font-style: italic;">Aucune description disponible</p>'}
        </div>

        ${currentProject.documents && currentProject.documents.length > 0 ? `
        <div class="section">
          <div class="section-title">Documents</div>
          <ul class="documents-list">
            ${currentProject.documents.map(doc => `
              <li>
                <strong>${doc.filename}</strong><br>
                <span style="color: #6b7280; font-size: 14px;">${doc.document_type} • ${(doc.file_size / (1024 * 1024)).toFixed(2)} MB</span>
              </li>
            `).join('')}
          </ul>
        </div>` : ''}

        <div style="margin-top: 60px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          <p>Plateforme CRESS Guadeloupe - Accompagnement ESS</p>
        </div>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `projet_${currentProject.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Note: User can print to PDF from the HTML file
    alert('Document HTML généré ! Ouvrez-le dans votre navigateur et utilisez "Imprimer > Enregistrer au format PDF" pour créer le PDF final.');
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Success Message */}
        {showSuccessMessage && location.state?.message && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-800 font-medium">{location.state.message}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour aux projets</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentProject.name}</h1>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentProject.status)}`}>
                  {getStatusLabel(currentProject.status)}
                </span>
                <span className="text-gray-500">
                  Créé le {new Date(currentProject.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Description */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description du projet</h2>
              {currentProject.description && !wizardData ? (
                <p className="text-gray-700">{currentProject.description}</p>
              ) : wizardData?.answers ? (
                <div className="space-y-4">
                  {Object.entries(wizardData.answers).map(([key, value]) => (
                    <div key={key} className="border-b border-gray-100 pb-3 last:border-b-0">
                      <dt className="text-sm font-medium text-gray-600 capitalize">
                        {key.replace(/_/g, ' ')}
                      </dt>
                      <dd className="text-gray-900 mt-1">{value as string}</dd>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">Aucune description disponible</p>
              )}
            </div>

            {/* Documents */}
            {currentProject.documents && currentProject.documents.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
                <div className="space-y-3">
                  {currentProject.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{doc.filename}</p>
                          <p className="text-sm text-gray-600">
                            {doc.document_type} • {(doc.file_size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        Voir
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Porteur</p>
                    <p className="font-medium text-gray-900">
                      {currentProject.owner?.full_name || currentProject.owner?.email || 'Non renseigné'}
                    </p>
                  </div>
                </div>
                
                {currentProject.assigned_advisor && (
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Accompagnateur</p>
                      <p className="font-medium text-gray-900">
                        {currentProject.assigned_advisor.full_name || currentProject.assigned_advisor.email}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Dernière mise à jour</p>
                    <p className="font-medium text-gray-900">
                      {new Date(currentProject.updated_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {(user?.id === currentProject.owner_id ||
                  [ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user?.profile_type as ProfileType)) && (
                  <button
                    onClick={() => navigate(`/projects/${currentProject.id}/edit`)}
                    className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <Edit className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Modifier le projet</span>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => navigate(`/projects/${currentProject.id}/documents`)}
                  className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Gérer les documents</span>
                  </div>
                </button>

                <button
                  onClick={handleExportPDF}
                  className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Download className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Exporter en PDF</span>
                  </div>
                </button>

                {user && [ProfileType.FINANCEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType) && (
                  <button
                    onClick={() => navigate(`/projects/${currentProject.id}/financing-analysis`)}
                    className="w-full text-left p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-900">Analyse Financement</span>
                    </div>
                  </button>
                )}

                {user && [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType) && (
                  <button
                    onClick={handleArchive}
                    disabled={isArchiving}
                    className="w-full text-left p-3 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center space-x-3">
                      <Archive className="w-5 h-5 text-orange-600" />
                      <span className="font-medium text-orange-900">
                        {isArchiving ? 'Archivage...' : 'Archiver le projet'}
                      </span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Funding Info */}
            {(currentProject.funding_requested || currentProject.funding_amount) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Financement</h3>
                <div className="space-y-3">
                  {currentProject.funding_requested && (
                    <div>
                      <p className="text-sm text-gray-600">Montant demandé</p>
                      <p className="text-xl font-bold text-gray-900">
                        {currentProject.funding_requested.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                  )}
                  {currentProject.funding_amount && (
                    <div>
                      <p className="text-sm text-gray-600">Montant accordé</p>
                      <p className="text-xl font-bold text-green-600">
                        {currentProject.funding_amount.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Project Health Score - For advisors and coordinators */}
            {user && [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType) && (
              <ProjectHealthScore projectId={currentProject.id} />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};